import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import ProfilePaymentMethods from '../components/ProfilePaymentMethods';
import {
  fetchPaymentMethodSession,
  getPaymentSessionErrorMessage,
  sessionShowsPaymentUpdated,
  sessionShowsReactivationWarning,
  startPaymentMethodSession,
} from '../utils/paymentMethodSessionApi';
import { consumeStripeSetupRedirect, peekStripeSetupRedirect } from '../utils/stripeSetupConfirm';
import { getContractApiUrl } from '../utils/apiUrls';
import '../components/UserProfile.css';
import './FormMagicLinkLanding.css';

const BACKEND_URL = getContractApiUrl();

const PaymentMethodMagicLinkLanding = () => {
  const { token } = useParams();
  const [session, setSession] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    peekStripeSetupRedirect();
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const data = await fetchPaymentMethodSession(token);
        if (data.status !== 'completed') {
          try {
            await startPaymentMethodSession(token);
          } catch {
            // Non-fatal: GET already validated the session; confirm still works.
          }
        }
        if (!cancelled) setSession(data);
      } catch (err) {
        if (!cancelled) {
          setError(getPaymentSessionErrorMessage(err));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  useEffect(() => {
    if (loading) return;
    if (error || !session || sessionShowsPaymentUpdated(session)) {
      consumeStripeSetupRedirect();
    }
  }, [loading, error, session]);

  if (loading) {
    return (
      <div className="form-magic-page">
        <div className="form-magic-card">Loading your payment method…</div>
      </div>
    );
  }

  if (error || !session) {
    return (
      <div className="form-magic-page">
        <div className="form-magic-card form-magic-error">
          <h2>Link unavailable</h2>
          <p>{error || 'This link is invalid or has expired.'}</p>
          <p>Email <a href="mailto:support@keyflo.ai">support@keyflo.ai</a> to request a new payment link.</p>
        </div>
      </div>
    );
  }

  if (sessionShowsPaymentUpdated(session)) {
    return (
      <div className="form-magic-page">
        <div className="form-magic-card">
          <h2>Payment method updated</h2>
          <p>You&apos;re all set. You can close this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="form-magic-page">
      <div className="form-magic-card" style={{ maxWidth: 560, textAlign: 'left' }}>
        <h2 style={{ marginTop: 0 }}>Update your payment method</h2>
        <p style={{ color: '#4B5563', lineHeight: 1.6 }}>
          Hi{session.name ? ` ${session.name.split(' ')[0]}` : ''}, use the secure form below
          to add or change the card on your KeyFlo account. You do not need to sign in.
        </p>
        {sessionShowsReactivationWarning(session) && (
          <p
            style={{
              background: '#FEF3C7',
              border: '1px solid #F59E0B',
              borderRadius: 8,
              color: '#92400E',
              lineHeight: 1.6,
              padding: '12px 14px',
            }}
          >
            Your KeyFlo subscription is cancelled. Updating your payment method
            will reactivate your account and resume billing.
          </p>
        )}
        <ProfilePaymentMethods
          user={{ email: session.email, name: session.name }}
          backendUrl={BACKEND_URL}
          sessionToken={token}
          onUpdated={() => setSession({ ...session, status: 'completed' })}
        />
      </div>
    </div>
  );
};

export default PaymentMethodMagicLinkLanding;
